/**
 * App header with navigation, search, alerts, chat badge, and account menu.
 */
import React, { useState, Fragment, useEffect } from "react";
import { Box, Card, Menu, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import MuiDrawer from "@mui/material/Drawer";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useLocation, useNavigate } from "react-router-dom";
import PusherClient from "pusher-js";
import { useTranslation } from "react-i18next";

import { VAvatar, VButton, VImage, VText } from "form";
import { useAsterController } from "context";
import { actionChatAdd, actionDarkMode, actionheaderSidenav, actionPeerChatAdd, actionPresenceLeave, actionPresencePing } from "context/action";
import { isOnline, subscribeNetwork } from "offline/network";
import routes from "routes";
import HeaderSearch from "./header.content/header.search";
import HeaderAlert from "./header.content/header.alert";
import HeaderMenu from "./header.content/header.menu";
import { BPopOver } from "components";
import OfflineToggle from "offline/OfflineToggle";

/**
 * Top navigation bar. Subscribes to Pusher for inbox messages and applies saved theme/language.
 */
const Header = () => {
  const { t, i18n } = useTranslation("common");

  const location = useLocation();
  const navigate = useNavigate();

  const [controller, dispatch] = useAsterController();
  const { headerSidenav, mobile, user, structure, chatList, preview } = controller;

  const [anchorEl, setAnchorEl] = useState(null);
  const [elemName, setElemName] = useState("");

  /**
   * Counts unread inbox messages.
   *
   * @param {Array} messages Chat messages.
   * @returns {number|null}
   */
  const getNewMsgCount = (messages) => {
    if (!messages) return null;
    return messages.filter((v) => v.is_new).length;
  };

  useEffect(() => {
    if (!user.id || !process.env.REACT_APP_PUSHER_KEY) return undefined;

    const site = process.env.REACT_APP_SITE_NAME;
    const pusherClient = new PusherClient(process.env.REACT_APP_PUSHER_KEY, { cluster: process.env.REACT_APP_PUSHER_CLUSTER });
    const channel = pusherClient.subscribe("message");

    const onAdminMessage = (res) => {
      if (!res || typeof res !== "object") return;
      if (res.room) {
        if (Number(res.from_id) !== Number(user.id)) {
          actionPeerChatAdd(dispatch, { ...res, peer_id: res.from_id, is_new: true });
        }
        return;
      }
      if (!res.to_admin && String(res.user_id ?? user.id) === String(user.id)) {
        const { text, urls, to_admin, created, updated, from_id } = res;
        actionChatAdd(dispatch, { user_id: user.id, text, urls, to_admin, from_id, created, updated, is_new: true });
      }
    };

    channel.bind(`${site}_${user.id}`, onAdminMessage);
    channel.bind(`${site}_typing`, (res) => {
      if (!res || typeof res !== "object") return;
      if (Number(res.from_id) === Number(user.id)) return;
      dispatch({ type: "SET_CHAT_TYPING", value: res });
      if (res.typing) {
        window.clearTimeout(channel._typingTimer);
        channel._typingTimer = window.setTimeout(() => {
          dispatch({ type: "SET_CHAT_TYPING", value: { ...res, typing: false } });
        }, 3000);
      }
    });
    channel.bind(`${site}_presence`, (res) => {
      if (!res || typeof res !== "object") return;
      dispatch({ type: "SET_PRESENCE", value: res });
    });

    const PRESENCE_STALE_MS = 90000;
    if (isOnline()) actionPresencePing(dispatch);
    else actionPresenceLeave(dispatch);
    const pingTimer = window.setInterval(() => {
      if (isOnline()) actionPresencePing(dispatch);
    }, 25000);
    const expireTimer = window.setInterval(() => {
      dispatch({ type: "EXPIRE_PRESENCE", value: Date.now() - PRESENCE_STALE_MS });
    }, 15000);
    const unsubNetwork = subscribeNetwork((online) => {
      if (online) actionPresencePing(dispatch);
      else actionPresenceLeave(dispatch);
    });
    const onPageHide = () => actionPresenceLeave(dispatch);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      try {
        window.clearInterval(pingTimer);
        window.clearInterval(expireTimer);
        window.removeEventListener("pagehide", onPageHide);
        unsubNetwork();
        window.clearTimeout(channel._typingTimer);
        actionPresenceLeave(dispatch);
        channel.unbind_all();
        pusherClient.unsubscribe("message");
        pusherClient.disconnect();
      } catch {
        /* ignore */
      }
    };
  }, [user.id, dispatch]);

  useEffect(() => {
    const lang = localStorage.getItem("language");
    i18n.changeLanguage(lang);

    const mode = localStorage.getItem("dark") === "true";
    if (mode) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
    actionDarkMode(dispatch, mode);
  }, [i18n, dispatch]);

  /**
   * Opens a named header popover from the clicked trigger.
   *
   * @param {React.MouseEvent<HTMLButtonElement>} e Click event.
   * @returns {void}
   */
  const onClick = (e) => {
    setAnchorEl(e.currentTarget);
    setElemName(e.currentTarget.name);
  };

  /**
   * Navigates to a top-level app route.
   *
   * @param {string} route Route path without a leading slash.
   * @returns {void}
   */
  const onRoute = (route) => navigate(`/${route}`);
  /**
   * Closes the open header popover.
   *
   * @returns {void}
   */
  const handleClose = () => setAnchorEl(null);

  /**
   * Renders desktop navigation list items from `routes`.
   *
   * @returns {JSX.Element}
   */
  const renderChildren = () => (
    <>
      <List component="nav" aria-labelledby="nested-lsit-subheader" className="flex gap-2">
        {routes.map((v) => (
          <Fragment key={v.route}>
            <ListItemButton onClick={() => onRoute(v.route)} className={`${location.pathname.includes(v.route) ? "v-bg-side-active" : ""} rounded-lg mt-2 gap-2 px-2 lg:px-4`}>
              <ListItemIcon className="text-2xl color-secondary min-w-fit">{v.icon}</ListItemIcon>
              <ListItemText className="hidden lg:block">
                <VText className="text-limit-1" div>
                  {t(v.text)}
                </VText>
              </ListItemText>
            </ListItemButton>
          </Fragment>
        ))}
      </List>
    </>
  );

  return (
    <Box className="flex v-bg-body">
      {mobile && (
        <MuiDrawer anchor="left" open={!headerSidenav} onClose={() => actionheaderSidenav(dispatch, true)}>
          <Box className="v-header w-60">
            <Link to={`/dashboard`} className="no-underline">
              <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${location.pathname.includes("dashboard") ? "v-bg-side-active" : ""}`}>
                <ListItemIcon className="text-2xl color-secondary min-w-fit">
                  <Icon icon="lucide:layout-dashboard" />
                </ListItemIcon>
                <ListItemText>
                  <VText>{t("Dashboard")}</VText>
                </ListItemText>
              </ListItemButton>
            </Link>
            <Link to={`/assigned`} className="no-underline flex items-center gap-2">
              <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${location.pathname.includes("assigned") ? "v-bg-side-active" : ""}`}>
                <ListItemIcon className="text-2xl color-secondary min-w-fit">
                  <Icon icon="codicon:book" />
                </ListItemIcon>
                <ListItemText>
                  <VText>{t("Assigned")}</VText>
                </ListItemText>
              </ListItemButton>
            </Link>
            <Link to={`/explore`} className="no-underline flex items-center gap-2">
              <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${location.pathname.includes("explore") ? "v-bg-side-active" : ""}`}>
                <ListItemIcon className="text-2xl color-secondary min-w-fit">
                  <Icon icon="lucide:clipboard-list" />
                </ListItemIcon>
                <ListItemText>
                  <VText>{t("Explore")}</VText>
                </ListItemText>
              </ListItemButton>
            </Link>
            <Link to={`/settings`} className="no-underline flex items-center gap-2">
              <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${location.pathname.includes("settings") ? "v-bg-side-active" : ""}`}>
                <ListItemIcon className="text-2xl color-secondary min-w-fit">
                  <Icon icon="ant-design:setting-outlined" />
                </ListItemIcon>
                <ListItemText>
                  <VText>{t("Settings")}</VText>
                </ListItemText>
              </ListItemButton>
            </Link>
            <Link to={`/profile`} className="no-underline flex items-center gap-2">
              <ListItemButton className={`flex rounded-lg mt-2 gap-2 ${location.pathname.includes("profile") ? "v-bg-side-active" : ""}`}>
                <ListItemIcon className="text-2xl color-secondary min-w-fit">
                  <Icon icon="healthicons:ui-user-profile-outline" />
                </ListItemIcon>
                <ListItemText>
                  <VText>{t("Profile")}</VText>
                </ListItemText>
              </ListItemButton>
            </Link>
          </Box>
        </MuiDrawer>
      )}
      <Box className="items-center justify-between h-[77px] px-9 w-full hidden v-header md:flex z-50">
        <Box className="flex items-center h-full">
          <List component="nav" aria-labelledby="nested-lsit-subheader">
            {["SUPERADMIN", "STAFF", "ADMIN"].includes(user.permission) ? (
              <Box className="flex items-center">
                <Link to="/dashboard" className="no-underline items-center gap-2">
                  <ListItemButton className={`v-bg-body dark:bg-gray-700 rounded-lg shadow py-2 w-60 flex justify-center`}>
                    <ListItemIcon>
                      <VImage src={process.env.REACT_APP_LOGO_URL} className="h-10" loading="eager" fetchpriority="high" />
                    </ListItemIcon>
                  </ListItemButton>
                </Link>
                <BPopOver
                  trigger={
                    <VButton iconOnly className="ml-1">
                      <Icon icon="akar-icons:chevron-down" />
                    </VButton>
                  }
                >
                <Box className="flex w-60 p-4">
                  <a href={process.env.REACT_APP_ADMIN_ENDPOINT} className="no-underline w-full" rel="noreferrer">
                    <VButton variant="outlined" color="primary" className="px-4 py-1 w-full">
                      <Icon icon="mdi:application-variable-outline" className="flex-shrink-0" />
                      &nbsp;{t("Admin")}
                    </VButton>
                  </a>
                  <a href={process.env.REACT_APP_STUDIO_ENDPOINT} className="no-underline ml-4 w-full" rel="noreferrer">
                    <VButton variant="outlined" color="primary" className="px-4 py-1 w-full">
                      <Icon icon="humbleicons:dashboard" className="flex-shrink-0" />
                      &nbsp;{t("Studio")}
                    </VButton>
                  </a>
                </Box>
              </BPopOver>
              </Box>
            ) : (
              <Link to="/dashboard" className="no-underline items-center gap-2">
                <ListItemButton className={`v-bg-body dark:bg-gray-700 rounded-lg shadow py-2 w-60 flex justify-center`}>
                  <ListItemIcon>
                    <VImage src={process.env.REACT_APP_LOGO_URL} className="h-10" loading="eager" fetchpriority="high" />
                  </ListItemIcon>
                </ListItemButton>
              </Link>
            )}
          </List>

          <Box className="w-px h-7 bg-gray-300 ml-5 mr-5" />

          {renderChildren()}
        </Box>
        <Box className="flex items-center h-full">
          <OfflineToggle />
          {preview && (
            <a href={`${process.env.REACT_APP_STUDIO_ENDPOINT}/course/${preview}`}>
              <VButton iconButton variant="outlined" color="secondary" className="mr-4" name="notification">
                <Icon icon="humbleicons:arrow-go-back" className="text-2xl" />
              </VButton>
            </a>
          )}
          {structure.course_id ? (
            <Link to={`/analysis/${structure.course_id}`} className={`${location.pathname.includes("course") || location.pathname.includes("content") ? "flex" : "hidden"} no-underline items-center gap-2`}>
              <VButton iconButton variant="outlined" color="secondary" className="mr-4" name="notification">
                <Icon className="text-2xl" icon="bx:bar-chart-square" />
              </VButton>
            </Link>
          ) : (
            <></>
          )}

          <Link to={`/inbox`} className="no-underline flex items-center gap-2 relative">
            <VButton iconButton variant="outlined" color="secondary" className="mr-4" name="notification">
              <Icon className="text-2xl" icon="fa-regular:envelope" />
            </VButton>
            {getNewMsgCount(chatList.messages) ? (
              <VText className="absolute -top-1 right-3 px-1 h-4 bg-primary rounded-full flex justify-center items-center text-xs text-white -ml-2 z-10" div style={{ minWidth: "16px" }}>
                {getNewMsgCount(chatList.messages)}
              </VText>
            ) : (
              <></>
            )}
          </Link>

          <VButton iconButton variant="outlined" color="secondary" onClick={onClick} className="mr-4" name="notification">
            <Icon className="text-2xl" icon="fa-regular:bell" />
          </VButton>

          <HeaderSearch />

          <Box className="w-px h-7 bg-gray-300 ml-4 mr-2" />

          <VButton variant="text" className="flex items-center normal-case w-20 lg:w-36" onClick={onClick} name="menu">
            <VAvatar size="sm" bgColor="light" src={user.avatar} imgProps={{ loading: "eager" }} />
            <Box className="ml-2 hidden lg:block text-left">
              <VText className="text-base font-bold leading-5 text-limit-1">{user.first_name || user.username || "-"}</VText>
              <VText className="text-xs text-gray-400 leading-4 text-limit-1 text-left">{t(user.permission) || "-"}</VText>
            </Box>
            <Icon className="text-gray-400 dark:text-gray-200 ml-auto" icon="akar-icons:chevron-down" />
          </VButton>

          <Menu id="basic-menu" className="v-drop-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} MenuListProps={{ "aria-labelledby": "basic-button" }} transformOrigin={{ horizontal: "right", vertical: "top" }} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
            {elemName === "notification" && <HeaderAlert />}
            {elemName === "menu" && <HeaderMenu handleClose={handleClose} />}
          </Menu>
        </Box>
      </Box>
      <Box className="h-[77px] w-full v-bg-body v-header block md:hidden z-50 p-2" id="v-header">
        <Card className="flex items-center justify-between h-full px-2">
          <Link to="/dashboard" className="no-underline items-center gap-2">
            <VImage src={process.env.REACT_APP_LOGO_URL} className="h-12" loading="eager" fetchpriority="high" />
          </Link>
          <Box className="flex items-center">
            <OfflineToggle />
            <VButton iconOnly color="secondary" className="ml-auto" onClick={() => actionheaderSidenav(dispatch, false)}>
              <Icon icon="heroicons-solid:menu-alt-1" className="text-[#667085] text-3xl" />
            </VButton>
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default Header;
